/**
 * The schedule dialog's Table tab (spec 05-TBL §5.1). It renders the SAME slots + dates
 * as the chart in the pinned column set — Property, UWI, Area, Formation, Status
 * (volume-source category), Permit, Spud, Completion, First production — sortable and
 * filterable per column (TanStack `DataTable`), virtualized with no cap or pagination.
 *
 * It forks nothing: the rows come from the dialog's ONE shared filtered row model
 * ({@link filterScheduleRows}, fed by the same search / phase / future-only atoms as the
 * chart); a cell edit routes through the SHARED edit layer ({@link useScheduleEdits}); and
 * the CSV round-trip's validation delegates its locked/order checks to the same rule module
 * the chart uses. The CSV export mirrors the live view (visible columns + active
 * sort/filter); an applied upload merges into the working log and marks the session dirty
 * (the 04-GNT out-of-date banner).
 */

import { useCallback, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { Button, FileUploaderDropContainer } from '@carbon/react';
import { Download } from '@carbon/react/icons';
import type { Column } from '@tanstack/react-table';
import {
  DataTable,
  useDataTableContext,
  type DataTableColumnDef,
} from 'components/data-table';
import { downloadBlob } from 'utils/download-blob';
import { MILESTONE_ORDER } from './constants';
import {
  clampIsoToNeighbors,
  filterScheduleRows,
  snapIsoToMonth,
} from './utils';
import {
  buildScheduleTableRows,
  dateColumnLabel,
  TABLE_TEXT_COLUMNS,
  TEXT_COLUMN_LABELS,
  type ScheduleTableRow,
  type ScheduleTextColumnId,
} from './table-model';
import {
  parseScheduleCsv,
  serializeScheduleCsv,
  validateScheduleCsv,
  type CsvExportColumn,
  type ParsedCsvRow,
} from './csv';
import { ScheduleDateCell } from './schedule-date-cell';
import { CsvPreviewModal } from './csv-preview-modal';
import { useScheduleEdits } from './use-schedule-edits';
import { futureOnlyAtom, phaseFilterAtom, searchQueryAtom } from './atoms';
import type {
  CsvValidationReport,
  MilestoneKey,
  ScheduleDialogPayload,
} from './types';
import styles from './schedule-table.module.scss';

export interface ScheduleTableProps {
  payload: ScheduleDialogPayload;
  /** Fired after an applied upload merges N writes into the working log (banner count). */
  onUploadApplied?: (changedCount: number) => void;
}

/** In-flight upload preview state (the parsed rows are kept so Apply can re-validate). */
interface PreviewState {
  fileName: string;
  parsedRows: ParsedCsvRow[] | null;
  fileError: string | null;
  report: CsvValidationReport | null;
}

/**
 * Maps the DataTable's visible leaf columns (in display order) to the export column spec,
 * so the CSV mirrors exactly what is on screen (respecting hide/show + reorder).
 *
 * @param {Column<ScheduleTableRow>[]} columns - The visible leaf columns.
 * @return {CsvExportColumn[]} The ordered export columns.
 */
function buildExportColumns(
  columns: Column<ScheduleTableRow>[]
): CsvExportColumn[] {
  const result: CsvExportColumn[] = [];
  for (const column of columns) {
    if ((MILESTONE_ORDER as string[]).includes(column.id)) {
      result.push({ kind: 'date', field: column.id as MilestoneKey });
    } else if ((TABLE_TEXT_COLUMNS as readonly string[]).includes(column.id)) {
      result.push({ kind: 'text', id: column.id as ScheduleTextColumnId });
    }
  }
  return result;
}

/**
 * The table toolbar's CSV actions: Export (client-built via `Papa.unparse`, mirroring the
 * live view) and Upload (drop container → the parent's parse/validate/preview flow).
 * Rendered inside the DataTable provider, so it reads the live sorted + filtered rows and
 * the visible columns from context.
 *
 * @param {object} props - The upload-file handler.
 * @return {JSX.Element} The toolbar actions.
 */
function ScheduleTableCsvActions({
  onFileSelected,
}: {
  onFileSelected: (file: File) => void;
}) {
  const { table } = useDataTableContext<ScheduleTableRow>();

  const handleExport = useCallback(() => {
    const columns = buildExportColumns(table.getVisibleLeafColumns());
    const rows = table.getSortedRowModel().rows.map((row) => row.original);
    const csv = serializeScheduleCsv(rows, columns);
    downloadBlob(
      new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
      'schedule-export.csv'
    );
  }, [table]);

  return (
    <div className={styles.csvActions}>
      <Button
        size="sm"
        kind="ghost"
        renderIcon={Download}
        onClick={handleExport}
      >
        Export CSV
      </Button>
      <FileUploaderDropContainer
        className={styles.uploader}
        accept={['.csv']}
        labelText="Drop or browse a .csv to upload"
        onAddFiles={(_event, { addedFiles }) => {
          const file = addedFiles?.[0] as File | undefined;
          if (file) {
            onFileSelected(file);
          }
        }}
      />
    </div>
  );
}

/**
 * Renders the schedule Table tab.
 *
 * @param {ScheduleTableProps} props - The schedule payload + the applied-upload callback.
 * @return {JSX.Element} The table.
 */
export function ScheduleTable({
  payload,
  onUploadApplied,
}: ScheduleTableProps) {
  const edits = useScheduleEdits();
  const search = useAtomValue(searchQueryAtom);
  const phaseFilter = useAtomValue(phaseFilterAtom);
  const futureOnly = useAtomValue(futureOnlyAtom);

  const [preview, setPreview] = useState<PreviewState | null>(null);

  // The dialog's ONE shared filtered row model, flattened to the pinned columns.
  const rows = useMemo(
    () =>
      buildScheduleTableRows(
        filterScheduleRows(payload.rows, edits.workingEntries, {
          search,
          phaseFilter,
          futureOnly,
          effectiveDate: payload.effectiveDate,
        }),
        payload.groups
      ),
    [
      payload.rows,
      payload.groups,
      payload.effectiveDate,
      edits.workingEntries,
      search,
      phaseFilter,
      futureOnly,
    ]
  );

  const { applyDrag } = edits;
  const order = payload.constraints.order;
  // Snap edits to the modeled month boundary when the calculator models month-start dates
  // (Defect 5): keeps a picked date on the same grid as every modeled date.
  const snapToMonth = payload.constraints.monthStartModeled;
  const columns = useMemo<DataTableColumnDef<ScheduleTableRow>[]>(() => {
    const dateColumn = (
      field: MilestoneKey
    ): DataTableColumnDef<ScheduleTableRow> => ({
      id: field,
      accessorFn: (row) => row.dates[field].value ?? '',
      header: dateColumnLabel(field),
      size: 150,
      meta: { type: 'date', label: dateColumnLabel(field) },
      cell: ({ row }) => (
        <ScheduleDateCell
          date={row.original.dates[field]}
          label={dateColumnLabel(field)}
          // Snap to the modeled month boundary, THEN clamp to the lifecycle-order neighbor
          // bounds — the SAME rules the chart drag applies — before routing to the shared
          // edit layer, so a table edit stays on-grid and can never land an out-of-order
          // override the chart could not produce.
          onCommit={(iso) =>
            applyDrag(
              row.original.slotId,
              field,
              clampIsoToNeighbors(
                row.original.dates,
                field,
                snapToMonth ? snapIsoToMonth(iso) : iso,
                order
              )
            )
          }
        />
      ),
    });

    const textColumn = (
      id: ScheduleTextColumnId,
      size: number
    ): DataTableColumnDef<ScheduleTableRow> => ({
      id,
      accessorKey: id,
      header: TEXT_COLUMN_LABELS[id],
      size,
      meta: { type: 'text', label: TEXT_COLUMN_LABELS[id] },
    });

    return [
      textColumn('wellName', 200),
      textColumn('uwi', 160),
      textColumn('unit', 140),
      textColumn('formation', 150),
      textColumn('status', 130),
      dateColumn('permitDate'),
      dateColumn('spudDate'),
      dateColumn('modeledCompletionDate'),
      dateColumn('firstProductionDate'),
    ];
  }, [applyDrag, order, snapToMonth]);

  const handleFileSelected = useCallback(
    async (file: File) => {
      const text = await file.text();
      const parsed = parseScheduleCsv(text);
      if (!parsed.ok) {
        setPreview({
          fileName: file.name,
          parsedRows: null,
          fileError: parsed.fileError,
          report: null,
        });
        return;
      }
      const { report } = validateScheduleCsv({
        parsedRows: parsed.rows,
        payload,
        workingEntries: edits.workingEntries,
      });
      setPreview({
        fileName: file.name,
        parsedRows: parsed.rows,
        fileError: null,
        report,
      });
    },
    [payload, edits.workingEntries]
  );

  const handleApply = useCallback(() => {
    if (!preview?.parsedRows) {
      setPreview(null);
      return;
    }
    // Re-validate against the CURRENT working log so a report gone stale never applies.
    const { report, writes } = validateScheduleCsv({
      parsedRows: preview.parsedRows,
      payload,
      workingEntries: edits.workingEntries,
    });
    if (report.rowErrors.length === 0 && writes.length > 0) {
      edits.applyCsvWrites(writes);
      // Report the changed-date count (not total writes) so the banner matches the
      // preview's "N dates change" line; reverts still dirty the session via the log.
      onUploadApplied?.(report.changedDateCount);
    }
    setPreview(null);
  }, [preview, payload, edits, onUploadApplied]);

  const csvActions = useMemo(
    () => [
      <ScheduleTableCsvActions
        key="csv-actions"
        onFileSelected={handleFileSelected}
      />,
    ],
    [handleFileSelected]
  );

  return (
    <div className={styles.container} data-testid="schedule-table">
      <DataTable<ScheduleTableRow>
        data={rows}
        columns={columns}
        getRowId={(row) => row.slotId}
        persistenceKey="eval3-schedule-table"
        config={{
          globalSearch: false,
          columnFilters: true,
          sorting: true,
          columnVisibility: true,
          columnReordering: true,
          columnPinning: false,
          export: false,
        }}
        rightToolbarActions={csvActions}
      />

      <CsvPreviewModal
        open={preview !== null}
        fileName={preview?.fileName ?? null}
        fileError={preview?.fileError ?? null}
        report={preview?.report ?? null}
        onApply={handleApply}
        onClose={() => setPreview(null)}
      />
    </div>
  );
}
